// pages/CPQHomePage.jsx
import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuote } from "./useQuote";
import {
    Container,
    Paper,
    Button,
    Typography,
    Box,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Snackbar,
    Alert,
    Grid,
    Card,
    CardContent,
    Divider,
    CircularProgress,
} from "@mui/material";
import Dynamics365NavigationBar from "./Dynamics365NavigationBar";
import apiClient from "../../Helpers/apiClient";

const CPQHomePage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { products, quoteId, setQuoteId, removeProduct, clearAll, getTotalValue } = useQuote();

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [currentStage, setCurrentStage] = useState(1); // 1 = Submit enabled, 2 = PDF enabled

    const [submissionResult, setSubmissionResult] = useState(null);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    // Get Quote ID from URL if available
    React.useEffect(() => {
        const params = new URLSearchParams(location.search);
        const id = params.get("id");
        if (id && !quoteId) {
            setQuoteId(id);
        }
    }, [location.search, quoteId, setQuoteId]);

    const generateCPQHomePDF = async () => {
        try {
            setIsSubmitting(true);

            const inputdata = {
                products,
                QuoteId: quoteId || null,
            };

            const response = await apiClient.post("/api/pdf/generate-cpqhome-pdf", inputdata, {
                responseType: "blob",
            });

            const blob = new Blob([response.data], { type: "application/pdf" });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = "Ecoprotector.pdf";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            clearAll();

            showNotification("Product added to quote and PDF downloaded successfully", "success");
            // Stage 2 -> Stage 1: After successful PDF generation
            setCurrentStage(1);
        } catch (err) {
            showNotification(err.response?.data?.message || "Failed to generate or download PDF", "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    const showNotification = (message, severity = "success") => {
        setSnackbar({ open: true, message, severity });
    };

    const handleSubmit = async () => {
        if (!products.length) {
            showNotification("No products to submit", "error");
            return;
        }

        if (!quoteId.trim()) {
            showNotification("Please enter a Quote ID", "error");
            return;
        }

        setIsSubmitting(true);

        try {
            // Format products for your existing batch API
            const formattedProducts = products.map((product) => ({
                quoteId: quoteId.trim(),
                productName: product.productName,
                partNumber: product.partNumber,
                pcs_DetailDescriptions: product.pcs_DetailDescriptions,
                quantity: product.quantity || 1,
                price: product.price || product.totalPrice,
            }));

            console.log("Sending request:", {
                quoteId: quoteId.trim(),
                products: formattedProducts,
            });

            const response = await apiClient.post("/api/Dynamics365EcoProtector/create-quote-product-bulk", {
                quoteId: quoteId.trim(),
                products: formattedProducts,
            });

            const result = response.data;
            console.log("Response received:", result);

            if (response.status >= 200 && response.status < 300) {
                setSubmissionResult({
                    success: true,
                    message: `Successfully submitted ${
                        result.details?.successfullyCreated || formattedProducts.length
                    } products!`,
                    details: result.details || result,
                });
                showNotification("Products submitted successfully!", "success");
                // Stage 1 -> Stage 2: After successful submission
                setCurrentStage(2);
                setSubmissionResult(null);
            } else {
                setSubmissionResult({
                    success: false,
                    message: `Submission failed: ${result.message || "Unexpected response"}`,
                    details: result,
                });
                showNotification("Submission failed", "error");
            }
        } catch (error) {
            console.error("Submission error:", error);

            let errorMessage = "Unknown error occurred";
            let errorDetails = null;

            if (error.response) {
                const errorData = error.response.data;
                console.log("Error response data:", errorData);

                if (typeof errorData === "string") {
                    errorMessage = errorData;
                } else if (errorData.message) {
                    errorMessage = errorData.message;

                    if (errorData.validationErrors && errorData.validationErrors.length > 0) {
                        errorMessage += "\n\nValidation errors:\n" + errorData.validationErrors.join("\n");
                    }
                } else {
                    errorMessage = "Server error occurred";
                }

                errorDetails = errorData;
            } else if (error.request) {
                errorMessage = "Network error - please check your connection";
            } else {
                errorMessage = error.message || "Request setup error";
            }

            setSubmissionResult({
                success: false,
                message: `Error: ${errorMessage}`,
                details: errorDetails,
            });
            showNotification("Submission failed", "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleRemoveProduct = (productId, productName) => {
        const confirmed = window.confirm(`Remove "${productName}" from quote?`);
        if (confirmed) {
            removeProduct(productId);
            showNotification(`${productName} removed from quote`);
        }
    };

    const handleClearAll = () => {
        const confirmed = window.confirm(
            `Are you sure you want to remove all ${products.length} products from the quote?\n\n`
        );
        if (confirmed) {
            clearAll();
            showNotification("Quote cleared");
            setCurrentStage(1); // Reset to stage 1
        }
    };

    const formatTimestamp = (timestamp) => {
        return new Date(timestamp).toLocaleString();
    };

    return (
        <Container maxWidth="xl">
            <Dynamics365NavigationBar />

            {/* Page Header */}
            <Box sx={{ mt: 3, mb: 3 }}>
                <Typography variant="h4" gutterBottom>
                    📋 CPQ Quote Builder
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Review your configured products and submit to Dynamics 365 Sales
                </Typography>
            </Box>

            {/* Quote Configuration */}
            <Paper sx={{ boxShadow: 3, borderRadius: 2, p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Quote Settings
                </Typography>
                <TextField
                    fullWidth
                    label="Dynamics 365 Quote ID"
                    value={quoteId}
                    onChange={(e) => setQuoteId(e.target.value)}
                    placeholder="Enter Quote GUID from Dynamics 365"
                    helperText="Get this ID from your Dynamics 365 Sales quote record"
                    sx={{ maxWidth: 600 }}
                />
            </Paper>

            {/* Quote Summary */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={8}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Quote Summary
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Box>
                                        <Typography variant="h4" color="primary">
                                            {products.length}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Products
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6}>
                                    <Box>
                                        <Typography variant="h4" color="primary">
                                            ${getTotalValue().toLocaleString()}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Total Value
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                    {products.length > 0 && (
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Actions
                                </Typography>
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    color="error"
                                    onClick={handleClearAll}
                                    disabled={currentStage === 2 || isSubmitting}
                                    sx={{ mb: 2 }}
                                >
                                    Clear All Products
                                </Button>
                            </CardContent>
                        </Card>
                    )}
                </Grid>
            </Grid>

            {/* Products List */}
            {products.length === 0 ? (
                <Paper
                    sx={{
                        boxShadow: 3,
                        borderRadius: 2,
                        p: 6,
                        textAlign: "center",
                    }}
                >
                    <Typography variant="h5" gutterBottom color="text.secondary">
                        📦 Your Quote is Empty
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }} color="text.secondary">
                        Start building your quote by configuring products.
                    </Typography>
                </Paper>
            ) : (
                <>
                    {/* Products Table */}
                    <Paper sx={{ boxShadow: 3, borderRadius: 2, mb: 3 }}>
                        <Box sx={{ p: 2 }}>
                            <Typography variant="h6">Products in Quote ({products.length})</Typography>
                        </Box>
                        <Divider />
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: "bold" }}>Product</TableCell>
                                        <TableCell sx={{ fontWeight: "bold" }}>Configuration</TableCell>
                                        <TableCell sx={{ fontWeight: "bold" }}>Part Number</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: "bold" }}>
                                            Quantity
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: "bold" }}>
                                            Price
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: "bold" }}>Added</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: "bold" }}>
                                            Actions
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {products.map((product) => (
                                        <TableRow key={product.id} hover>
                                            <TableCell>
                                                <Box>
                                                    <Typography
                                                        variant="body2"
                                                        color="black"
                                                        sx={{
                                                            fontWeight: "bold",
                                                        }}
                                                    >
                                                        {product.productType}
                                                    </Typography>
                                                    <Typography variant="subtitle2">
                                                        {product.productName}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Box>
                                                    <Typography variant="body2">
                                                        {product.modelConfig?.configurationSummary ||
                                                            "Standard Configuration"}
                                                    </Typography>
                                                    <details
                                                        style={{
                                                            marginTop: "8px",
                                                        }}
                                                    >
                                                        <summary
                                                            style={{
                                                                cursor: "pointer",
                                                                fontSize: "16px",
                                                                color: "#666",
                                                            }}
                                                        >
                                                            View Details
                                                        </summary>
                                                        <Box
                                                            sx={{
                                                                mt: 1,
                                                                p: 1,
                                                                bgcolor: "grey.50",
                                                                borderRadius: 1,
                                                            }}
                                                        >
                                                            <Typography variant="caption" display="block">
                                                                <strong>Description:</strong>
                                                            </Typography>
                                                            <Typography
                                                                variant="caption"
                                                                display="block"
                                                                sx={{ mb: 1 }}
                                                            >
                                                                {product.pcs_DetailDescriptions}
                                                            </Typography>
                                                            {product.priceBreakdown && (
                                                                <>
                                                                    <Typography
                                                                        variant="caption"
                                                                        display="block"
                                                                    >
                                                                        <strong>Price Breakdown:</strong>
                                                                    </Typography>
                                                                    <pre
                                                                        style={{
                                                                            fontSize: "16px",
                                                                            margin: 0,
                                                                        }}
                                                                    >
                                                                        {JSON.stringify(
                                                                            product.priceBreakdown,
                                                                            null,
                                                                            2
                                                                        )}
                                                                    </pre>
                                                                </>
                                                            )}
                                                        </Box>
                                                    </details>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <code style={{ fontSize: "16px" }}>{product.partNumber}</code>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography variant="body2">{product.quantity}</Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography variant="subtitle2">
                                                    ${product.totalPrice?.toLocaleString()}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="caption" color="text.secondary">
                                                    {formatTimestamp(product.addedAt)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Button
                                                    size="small"
                                                    color="error"
                                                    disabled={currentStage === 2 || isSubmitting}
                                                    onClick={() =>
                                                        handleRemoveProduct(product.id, product.productName)
                                                    }
                                                >
                                                    Remove
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>

                    {/* Submit Section */}
                    <Paper
                        sx={{
                            p: 3,
                            boxShadow: 3,
                            borderRadius: 2,
                            mb: 3,
                        }}
                    >
                        <Typography variant="h6" gutterBottom>
                            Submit to Dynamics 365
                        </Typography>

                        {!quoteId.trim() && (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                Please enter a Quote ID above to submit products
                            </Alert>
                        )}

                        {products.length === 0 && (
                            <Alert severity="info" sx={{ mb: 2 }}>
                                Add products to your quote to enable submission
                            </Alert>
                        )}

                        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                            <Button
                                size="large"
                                variant="contained"
                                onClick={handleSubmit}
                                disabled={
                                    isSubmitting ||
                                    !quoteId.trim() ||
                                    products.length === 0 ||
                                    currentStage !== 1
                                }
                                startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
                            >
                                {isSubmitting
                                    ? `Submitting ${products.length} Products...`
                                    : products.length === 0
                                    ? "Submit Products to Quote"
                                    : `Submit All ${products.length} Products to Quote`}
                            </Button>

                            <Button
                                size="large"
                                variant="contained"
                                color="secondary"
                                onClick={generateCPQHomePDF}
                                disabled={
                                    isSubmitting ||
                                    !quoteId.trim() ||
                                    products.length === 0 ||
                                    currentStage !== 2
                                }
                                startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
                            >
                                {isSubmitting
                                    ? "Generating Quote PDF..."
                                    : products.length === 0
                                    ? "Generate Quote PDF"
                                    : `Generate Quote PDF (${products.length} items)`}
                            </Button>
                        </Box>
                    </Paper>
                </>
            )}

            {/* Submission Result */}
            {submissionResult && (
                <Paper
                    sx={{
                        boxShadow: 3,
                        borderRadius: 2,
                        mt: 3,
                        p: 3,
                        bgcolor: submissionResult.success ? "success.light" : "error.light",
                        color: submissionResult.success ? "success.contrastText" : "error.contrastText",
                    }}
                >
                    <Typography variant="h6" gutterBottom>
                        {submissionResult.success ? "Submission Successful!" : "Submission Failed"}
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        {submissionResult.message}
                    </Typography>
                    {submissionResult.details && (
                        <details>
                            <summary style={{ cursor: "pointer" }}>Technical Details</summary>
                            <pre style={{ fontSize: "12px", marginTop: "8px" }}>
                                {JSON.stringify(submissionResult.details, null, 2)}
                            </pre>
                        </details>
                    )}
                    <Button
                        variant="outlined"
                        color="inherit"
                        onClick={() => setSubmissionResult(null)}
                        sx={{ mt: 2 }}
                    >
                        Dismiss
                    </Button>
                </Paper>
            )}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default CPQHomePage;
